import { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { KeyValue } from '@mews/enums';
import { ANIMATION_DURATION } from '@optimus-web/common';
import { Portal } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { useFocusTrap } from '../hooks';
import { ImageProps } from '../Image';
import { ImageItem } from '../types';
import { mod } from '../utils/func';
import { ImageList as ImageListComponent, ImageListProps } from './ImageList';
import {
    BackdropElement,
    CloseIconButtonElement,
    DescriptionWrapperElement,
    ImageCounterElement,
    ImageDescriptionElement,
    ImageListWrapperElement,
    LightboxDialogElement,
    LightboxImageElement,
    LightboxImageWithControlsElement,
    LightboxImageWrapperElement,
    LightboxSwipeableElement,
    LightboxTransitionGroupElement,
    LightboxWrapperElement,
    SideIconButtonElement,
} from './style';

export interface LightboxProps {
    images: Array<ImageItem>;
    onClose?: (imageIndex: number) => void;
    small?: boolean;
    isImagePreviewEnabled?: boolean;
    portalContainerId?: string;
    initialImageId?: string;
    closeIconButtonLabel?: React.ReactChild;
    prevIconButtonLabel?: React.ReactChild;
    nextIconButtonLabel?: React.ReactChild;
}

interface LightboxFactory {
    LightboxImage?: ComponentType<ImageProps>;
    ImageList?: ComponentType<ImageListProps>;
}

export const lightboxFactory = ({
    ImageList = ImageListComponent,
    LightboxImage = LightboxImageElement,
}: LightboxFactory = {}) => {
    const Lightbox = ({
        images,
        small,
        isImagePreviewEnabled = true,
        onClose,
        portalContainerId,
        initialImageId,
        closeIconButtonLabel,
        prevIconButtonLabel,
        nextIconButtonLabel,
        ...other
    }: LightboxProps) => {
        const imageIds = useMemo(() => images.map((image) => image.id), [images]);

        const lightboxRef = useRef<HTMLDivElement>(null);
        const [lightboxElement, setLightboxElement] = useState<HTMLElement | undefined>();

        useFocusTrap(lightboxElement);

        useEffect(() => {
            if (lightboxRef.current !== null) {
                lightboxRef.current.focus();
                setLightboxElement(lightboxRef.current);
            }
        }, [lightboxRef]);

        const getInitialImageIndex = () => {
            if (initialImageId && imageIds.includes(initialImageId)) {
                return imageIds.indexOf(initialImageId);
            }
            return 0;
        };

        const [imageIndex, setImageIndex] = useState(getInitialImageIndex());

        if (images.length === 0) {
            return null;
        }

        const handleClose = () => {
            if (onClose) {
                onClose(imageIndex);
            }
        };

        const selectNextImage = () => setImageIndex(mod(imageIndex + 1, images.length));
        const selectPreviousImage = () => setImageIndex(mod(imageIndex - 1, images.length));

        const selectImageFromPreview = (imageId: string) => {
            const selectedImageIndex = imageIds.indexOf(imageId);
            setImageIndex(selectedImageIndex);
        };

        const handleImageSwipedLeft = () => selectNextImage();
        const handleImageSwipedRight = () => selectPreviousImage();

        const handleKeyboardEvent = (event: React.KeyboardEvent<HTMLDivElement>) => {
            switch (event.key) {
                case KeyValue.Escape:
                    handleClose();
                    break;
                case KeyValue.ArrowLeft:
                    selectPreviousImage();
                    break;
                case KeyValue.ArrowRight:
                    selectNextImage();
                    break;
                default:
                    break;
            }
        };

        const { id, description, ...imageProps } = images[imageIndex];

        return (
            <Portal containerId={portalContainerId} {...other}>
                <LightboxDialogElement
                    ref={lightboxRef}
                    tabIndex={-1}
                    role="dialog"
                    data-test-lightbox
                    onKeyUp={handleKeyboardEvent}
                >
                    <BackdropElement light />
                    <CloseIconButtonElement
                        data-test-close
                        variant="ghost"
                        icon={IconName.CrossClose}
                        onClick={handleClose}
                        small={small}
                        accessibleText={closeIconButtonLabel}
                        type="button"
                    />
                    <LightboxWrapperElement>
                        <LightboxSwipeableElement
                            onSwipedLeft={handleImageSwipedLeft}
                            onSwipedRight={handleImageSwipedRight}
                        >
                            <LightboxImageWithControlsElement small={small}>
                                {!small && (
                                    <>
                                        <SideIconButtonElement
                                            data-test-image-previous
                                            variant="ghost"
                                            icon={IconName.ChevronLeft}
                                            onClick={selectPreviousImage}
                                            accessibleText={prevIconButtonLabel}
                                            type="button"
                                        />
                                        <SideIconButtonElement
                                            data-test-image-next
                                            variant="ghost"
                                            right
                                            icon={IconName.ChevronRight}
                                            onClick={selectNextImage}
                                            accessibleText={nextIconButtonLabel}
                                            type="button"
                                        />
                                    </>
                                )}
                                <LightboxTransitionGroupElement timeoutEnter={ANIMATION_DURATION.fast} appear>
                                    <LightboxImageWrapperElement key={id} aria-live="polite">
                                        <LightboxImage {...imageProps} shouldShowLoader />
                                    </LightboxImageWrapperElement>
                                </LightboxTransitionGroupElement>
                            </LightboxImageWithControlsElement>
                        </LightboxSwipeableElement>
                        <DescriptionWrapperElement>
                            <ImageCounterElement>{`${imageIndex + 1}/${images.length}`}</ImageCounterElement>
                            {description && <ImageDescriptionElement>{description}</ImageDescriptionElement>}
                        </DescriptionWrapperElement>
                        {isImagePreviewEnabled && (
                            <ImageListWrapperElement small={small}>
                                <ImageList
                                    images={images}
                                    currentId={id}
                                    onSelectedImageChange={selectImageFromPreview}
                                />
                            </ImageListWrapperElement>
                        )}
                    </LightboxWrapperElement>
                </LightboxDialogElement>
            </Portal>
        );
    };

    return Lightbox;
};

export default lightboxFactory();
